type AC = { long_name: string; short_name: string; types: string[] };

interface PlaceResult {
  types?: string[];
  address_components?: AC[];
  name?: string;
  formatted_address?: string;
}

/**
 * Converts a Google Places result into a clean, level-aware display string.
 *
 * The level of detail mirrors what the user actually selected:
 *   Country       → "France"
 *   State/Region  → "Brittany, France"  (admin_area_level_1)
 *   County        → "Yarra Valley, Australia"  (admin_area_level_2)
 *   City/Town     → "Paris, France" | "Melbourne, Australia"
 *   Suburb        → "St Kilda East, Australia"  (sublocality)
 *
 * The level is whatever was PICKED, never a tidier one nearby. Select
 * St Kilda East and the destination is St Kilda East, not Melbourne; select
 * Canggu and it's Canggu, not Bali; select Bali and it's Bali. A traveller
 * choosing a suburb has said something more specific than the city, and
 * rolling it up throws that away.
 *
 * Postal codes and state/province abbreviations are never included.
 */
export function formatPlaceLabel(place: PlaceResult): string {
  const components = place.address_components ?? [];
  const types = place.types ?? [];

  const get = (type: string, k: 'long_name' | 'short_name' = 'long_name'): string | null =>
    components.find(c => c.types.includes(type))?.[k] ?? null;

  const country  = get('country');
  const state    = get('administrative_area_level_1');
  const locality = get('locality') ?? get('postal_town');
  const sub      = get('sublocality_level_1') ?? get('sublocality');
  const hood     = get('neighborhood');

  const join = (...parts: (string | null | undefined)[]) =>
    parts.filter(Boolean).join(', ');

  if (types.includes('country'))
    return country ?? place.name ?? '';

  if (types.includes('administrative_area_level_1'))
    return join(state, country);

  if (types.includes('administrative_area_level_2'))
    return join(get('administrative_area_level_2'), country);

  if (types.some(t => ['locality', 'postal_town', 'administrative_area_level_3'].includes(t)))
    return join(locality ?? get('administrative_area_level_3'), country);

  if (types.some(t => t.startsWith('sublocality') || t === 'neighborhood')) {
    // The suburb and the country, not the city in between: picking a suburb
    // is a more specific choice than the city, and naming both reads as
    // indecision. Country stays because currency detection and the Gemini
    // prompts both need it.
    return join(sub ?? hood, country);
  }

  // Fallback: name + country
  const name = place.name ?? '';
  if (name && country && name !== country) return `${name}, ${country}`;

  // Last resort: tidy whatever text we have.
  return tidyPlaceText(place.formatted_address ?? name);
}

/**
 * Clean up a place string that never went through Google's structured data —
 * a hand-typed destination, or the description Google's autocomplete widget
 * writes into the input when a suggestion is highlighted but not chosen.
 *
 * That description is where "Daylesford VIC" came from: the formatter itself
 * has never emitted a state, but the raw text does, and committing it on blur
 * put it straight onto the destination.
 *
 * Removes postcodes, and a bare state or province abbreviation sitting
 * between the place and its country. Only an abbreviation — two or three
 * capitals with no lowercase — so real names are safe: Bali, Goa and Fez
 * aren't uppercase, and NSW, VIC, CA, NY, ON and BC are.
 */
export function tidyPlaceText(raw: string): string {
  return (raw || '')
    // Postcodes: numeric, then the two alphanumeric shapes — Canada's
    // A1A 1A1 and the UK's SW1A 1AA — written separately because one pattern
    // loose enough for both also eats ordinary words.
    .replace(/,?\s*\b\d{3,}\b/g, '')
    .replace(/,?\s*\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/g, '')
    .replace(/,?\s*\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/g, '')
    // A standalone state/province abbreviation — only when something follows
    // it. The last segment is the country, and USA, UAE and UK are countries,
    // not states.
    .replace(/(^|[\s,])[A-Z]{2,3}(?=\s*,)/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+,/g, ',')
    .replace(/,\s*,/g, ',')
    .replace(/^[,\s]+|[,\s]+$/g, '')
    .trim();
}
